import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Building2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { podeAbrirRota } from '@/app/registry/routes';
import { useClientesToken } from '@/hooks/tributacao/useClientesToken';
import { formatarDocumento } from '@/lib/fiscal/sped/documentos';
import { LISTA_ATALHOS } from '@/app/shell/atalhos';
import { menuGroups, type MenuGroup, type MenuItem, type SubMenuItem } from '@/app/registry/menu';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

type CommandRoute = {
  group: string;
  href: string;
  name: string;
  pathLabel: string;
  permission: string | string[];
};

type NavNode = MenuItem | SubMenuItem;

const hasAnyPermission = (permission: string | string[], hasPermission: (permission: string) => boolean) =>
  Array.isArray(permission) ? permission.some((item) => hasPermission(item)) : hasPermission(permission);

const flattenItems = (
  groupName: string,
  items: NavNode[],
  hasPermission: (permission: string) => boolean,
  parents: string[] = [],
): CommandRoute[] => {
  return items.flatMap((item) => {
    const children = item.subItems ? flattenItems(groupName, item.subItems, hasPermission, [...parents, item.name]) : [];
    const allowed = hasAnyPermission(item.permission, hasPermission);
    const current =
      item.href !== '#' && allowed
        ? [
            {
              group: groupName,
              href: item.href,
              name: item.name,
              pathLabel: [...parents, item.name].join(' / '),
              permission: item.permission,
            },
          ]
        : [];

    return [...current, ...children];
  });
};

const buildRoutes = (groups: MenuGroup[], hasPermission: (permission: string) => boolean): CommandRoute[] => {
  return [
    ...groups.flatMap((group) => flattenItems(group.name, group.items, hasPermission)),
  ];
};

type CommandPaletteProps = {
  onOpenChange: (open: boolean) => void;
  open: boolean;
};

const CommandPalette: React.FC<CommandPaletteProps> = ({ onOpenChange, open }) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const routes = useMemo(() => buildRoutes(menuGroups, hasPermission), [hasPermission]);
  // Clientes abrem o Perfil fiscal já no cliente. Só carrega com a busca aberta e para quem pode abrir essa tela.
  const podeVerClientes = podeAbrirRota('/tributacao/perfil-fiscal', hasPermission);
  const { data: clientes = [] } = useClientesToken({ enabled: open && podeVerClientes });
  const groupedRoutes = useMemo(() => {
    return routes.reduce<Record<string, CommandRoute[]>>((acc, route) => {
      acc[route.group] = acc[route.group] || [];
      acc[route.group].push(route);
      return acc;
    }, {});
  }, [routes]);

  const goToRoute = (href: string) => {
    navigate(href);
    onOpenChange(false);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder={podeVerClientes ? 'Buscar tela ou empresa (nome ou CNPJ)…' : 'Buscar tela…'} />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>Nada encontrado.</CommandEmpty>
        {Object.entries(groupedRoutes).map(([group, groupRoutes]) => (
          <CommandGroup key={group} heading={group}>
            {groupRoutes.map((route) => (
              <CommandItem
                key={`${route.group}:${route.href}:${route.name}`}
                value={`${route.group} ${route.pathLabel} ${route.href}`}
                onSelect={() => goToRoute(route.href)}
                className="gap-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{route.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{route.pathLabel}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
        {podeVerClientes && clientes.length > 0 ? (
          <CommandGroup heading="Empresas">
            {clientes.map((cliente) => (
              <CommandItem
                key={cliente.cnpj}
                value={`cliente ${cliente.nome} ${cliente.cnpj} ${formatarDocumento(cliente.cnpj)}`}
                onSelect={() => goToRoute(`/tributacao/perfil-fiscal?cliente=${cliente.cnpj}`)}
                className="gap-2"
              >
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{cliente.nome}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    <span className="font-mono">{formatarDocumento(cliente.cnpj)}</span> · abrir o Perfil fiscal
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
      </CommandList>
      <div className="flex flex-wrap gap-x-4 gap-y-1 border-t px-3 py-2 text-xs text-muted-foreground">
        {LISTA_ATALHOS.map((atalho) => (
          <span key={atalho.teclas}>
            <kbd className="rounded border bg-muted px-1 py-0.5 font-sans text-[11px] text-foreground">{atalho.teclas}</kbd> {atalho.acao}
          </span>
        ))}
      </div>
    </CommandDialog>
  );
};

export default CommandPalette;
